import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaUpload, FaHandshake, FaCheckCircle,
    FaTimes, FaEraser, FaCalendarAlt, FaSync,
    FaExclamationTriangle, FaFileInvoiceDollar,
    FaWallet, FaArchive,
    FaBolt, FaFilter,
    FaUniversity, FaChartBar, FaSpinner,
    FaPause, FaPlay,
} from 'react-icons/fa';
import './ConciliacaoBancaria.css';

/* ─── HELPER: extrai tenantId do JWT local ──────────────────────────────── */
function getLocalTenantId() {
    try {
        const token = localStorage.getItem('token');
        if (!token) return 'guest';
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64));
        return String(payload.cliente_id ?? payload.tenantId ?? 'guest');
    } catch {
        return 'guest';
    }
}

/* ─── CHAVES DE STORAGE NAMESPADAS POR TENANT ───────────────────────────── */
// v4 = migração intencional: descarta dados v3 sem namespace que podem estar
//      contaminados com registros de outros tenants.
const _CNC_TID     = getLocalTenantId();
const STORAGE_BANK = `cnc_bank_v4_${_CNC_TID}`;
const STORAGE_SYS  = `cnc_sys_v4_${_CNC_TID}`;
const STORAGE_HIST = `cnc_hist_v4_${_CNC_TID}`;

/* ─── FORMATTERS ─────────────────────────────────────────────── */
const fmtCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const fmtDate = (s) => {
    if (!s) return '—';
    const d = s.includes('T') ? s : `${s}T00:00:00`;
    return new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
    });
};

const normDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

/* ─── CONSTANTE DE POLLING ───────────────────────────────────── */
const POLL_INTERVAL = 60; // segundos

/* ─── STAMP EFFECT ───────────────────────────────────────────── */
const StampEffect = () => (
    <div className="se-overlay">
        <div className="se-circle">
            <svg viewBox="0 0 60 60" className="se-svg" fill="none" stroke="currentColor">
                <circle cx="30" cy="30" r="26" strokeWidth="3" className="se-ring" />
                <path
                    d="M 16 30 L 26 40 L 44 20"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="se-check"
                />
            </svg>
        </div>
        <p className="se-label">Conciliado</p>
    </div>
);

/* ─── LOADING OVERLAY ────────────────────────────────────────── */
const LoadingOverlay = () => (
    <div className="cnc-loader">
        <div className="cnc-loader-inner">
            <div className="cnc-loader-ring" />
            <FaSync className="cnc-loader-icon" />
        </div>
        <span>Sincronizando...</span>
    </div>
);

/* ─── TX CARD (bank side) ────────────────────────────────────── */
const BankTxCard = ({ tx, isSelected, isMatch, onSelect, onQuickMatch, matchTarget, onRemove }) => (
    <div
        className={[
            'tx-card bank',
            isSelected ? 'sel'   : '',
            isMatch    ? 'match' : '',
            tx.origem  === 'baas_wallet' ? 'baas-origin' : '',
        ].filter(Boolean).join(' ')}
        onClick={onSelect}
    >
        <div className="tx-card-bar bank-bar" />
        <div className="tx-card-body">
            <div className="tx-meta">
                <span className="tx-date">{fmtDate(tx.data)}</span>
                {tx.origem === 'baas_wallet' && (
                    <span className="tx-baas-badge" title="Transação da Conta Digital BaaS">
                        <FaUniversity size={9} /> Conta Digital
                    </span>
                )}
                {isMatch && (
                    <span className="tx-match-badge">
                        <FaBolt /> Par Sugerido
                    </span>
                )}
            </div>
            <p className="tx-desc">{tx.descricao}</p>
        </div>
        <div className="tx-card-right">
            <strong className={`tx-amount ${tx.valor >= 0 ? 'pos' : 'neg'}`}>
                {fmtCurrency(tx.valor)}
            </strong>
            <div className="tx-actions">
                {isMatch && (
                    <button
                        className="tx-btn-quick"
                        title="Conciliar este par"
                        onClick={(e) => { e.stopPropagation(); onQuickMatch(tx, matchTarget); }}
                    >
                        <FaCheckCircle />
                    </button>
                )}
                <button
                    className="tx-btn-del"
                    title="Remover"
                    onClick={(e) => { e.stopPropagation(); onRemove(tx.frontendId); }}
                >
                    <FaTimes />
                </button>
            </div>
        </div>
    </div>
);

/* ─── TX CARD (system side) ──────────────────────────────────── */
const SystemTxCard = ({ tx, isSelected, isMatch, onSelect }) => {
    const val      = tx.tipo === 'Despesa' && tx.valor > 0 ? -tx.valor : tx.valor;
    const tagClass = { caixa: 'tag-caixa', pagar: 'tag-pagar', receber: 'tag-receber' }[tx.origem_tipo] || 'tag-caixa';

    return (
        <div
            className={`tx-card system ${isSelected ? 'sel' : ''} ${isMatch ? 'match' : ''}`}
            onClick={onSelect}
        >
            <div className={`tx-card-bar sys-bar ${tx.origem_tipo}`} />
            <div className="tx-card-body">
                <div className="tx-meta">
                    <span className={`tx-origin-tag ${tagClass}`}>{tx.origem_tipo || 'caixa'}</span>
                    <span className="tx-date">{fmtDate(tx.data_ref || tx.data)}</span>
                    {isMatch && (
                        <span className="tx-match-badge sys">
                            <FaBolt /> Par Sugerido
                        </span>
                    )}
                </div>
                <p className="tx-desc">{tx.descricao}</p>
            </div>
            <strong className={`tx-amount ${val >= 0 ? 'pos' : 'neg'}`}>
                {fmtCurrency(val)}
            </strong>
        </div>
    );
};

/* ─── KPI CHIP ───────────────────────────────────────────────── */
const KpiChip = ({ icon: Icon, label, value, accent }) => (
    <div className="kpi-chip" style={{ '--accent': accent }}>
        <div className="kpi-chip-icon"><Icon /></div>
        <div>
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    </div>
);

/* ─── HISTORY MODAL ──────────────────────────────────────────── */
const HistoryModal = ({ history, onClose }) => (
    <div className="cnc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="cnc-modal archive-modal">
            <div className="cnc-modal-head">
                <div className="modal-head-icon"><FaArchive /></div>
                <div>
                    <h3>Arquivo Digital</h3>
                    <p>{history.length} registros conciliados</p>
                </div>
                <button className="modal-close" onClick={onClose}><FaTimes /></button>
            </div>
            <div className="cnc-modal-body">
                {history.length === 0 ? (
                    <div className="modal-empty">
                        <FaArchive />
                        <p>Nenhum registro conciliado nesta sessão.</p>
                    </div>
                ) : (
                    <div className="archive-table-wrap">
                        <table className="archive-table">
                            <thead>
                                <tr>
                                    <th>Conciliado em</th>
                                    <th>Extrato / Origem</th>
                                    <th>Registro CRM</th>
                                    <th>Valor</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((item, i) => (
                                    <tr key={i}>
                                        <td className="td-date">
                                            {new Date(item.reconciledAt).toLocaleString('pt-BR')}
                                        </td>
                                        <td>
                                            {item.bank.origem === 'baas_wallet' && (
                                                <span className="tx-baas-badge sm" style={{ marginRight: 5 }}>
                                                    <FaUniversity size={8} />
                                                </span>
                                            )}
                                            {item.bank.descricao}
                                        </td>
                                        <td>{item.system.descricao}</td>
                                        <td className={item.bank.valor >= 0 ? 'td-pos' : 'td-neg'}>
                                            {fmtCurrency(item.bank.valor)}
                                        </td>
                                        <td><span className="status-ok">✓ Baixado</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="cnc-modal-foot">
                <button className="btn-modal-close" onClick={onClose}>Fechar Dossiê</button>
            </div>
        </div>
    </div>
);

/* ─── ADJUST MODAL ───────────────────────────────────────────── */
const AdjustModal = ({ onConfirm, onClose, adjustmentType, setAdjustmentType }) => (
    <div className="cnc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="cnc-modal adjust-modal">
            <div className="adjust-warning">
                <div className="aw-icon"><FaExclamationTriangle /></div>
                <h3>Divergência de Valores</h3>
                <p>Os valores diferem. Como deseja tratar a diferença?</p>
            </div>
            <div className="adjust-options">
                {[
                    { value: 'juros',    label: 'Juros / Multa',   desc: 'Lançar a diferença como encargo financeiro no caixa.' },
                    { value: 'desconto', label: 'Desconto Obtido', desc: 'Registrar como abatimento/desconto comercial.' },
                ].map(opt => (
                    <label
                        key={opt.value}
                        className={`adjust-opt ${adjustmentType === opt.value ? 'sel' : ''}`}
                    >
                        <input
                            type="radio"
                            value={opt.value}
                            checked={adjustmentType === opt.value}
                            onChange={() => setAdjustmentType(opt.value)}
                            hidden
                        />
                        <div className="ao-radio" />
                        <div>
                            <strong>{opt.label}</strong>
                            <span>{opt.desc}</span>
                        </div>
                    </label>
                ))}
            </div>
            <div className="adjust-actions">
                <button className="btn-adj-cancel"  onClick={onClose}>Cancelar</button>
                <button className="btn-adj-confirm" onClick={onConfirm}>
                    Confirmar e Baixar Título
                </button>
            </div>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const ConciliacaoBancaria = () => {
    const location = useLocation();

    /* ── UI state ─────────────────────────────────────────────── */
    const [viewMode,    setViewMode]    = useState('diario');
    const [loading,     setLoading]     = useState(false);
    const [showStamp,   setShowStamp]   = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showAdjust,  setShowAdjust]  = useState(false);
    const [adjustType,  setAdjustType]  = useState('juros');
    const [showKpiBar,  setShowKpiBar]  = useState(false);

    /* ── BaaS state ───────────────────────────────────────────── */
    const [hasBaas,        setHasBaas]        = useState(false);
    const [baasWallet,     setBaasWallet]      = useState(null);
    const [baasReconciling,setBaasReconciling] = useState(false);
    const [showBaasBar,    setShowBaasBar]    = useState(false);

    /* ── Polling state ────────────────────────────────────────── */
    const [pollingActive, setPollingActive] = useState(false);
    const [lastSync,      setLastSync]      = useState(null);
    const [pollCountdown, setPollCountdown] = useState(POLL_INTERVAL);

    /* ── Transações (persistidas em localStorage) ─────────────── */
    const [bankTxs, setBankTxs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_BANK) || '[]'); } catch { return []; }
});
const [sysTxs, setSysTxs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_SYS) || '[]'); } catch { return []; }
});
const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_HIST) || '[]'); } catch { return []; }
});

    /* ── Seleção / filtro ─────────────────────────────────────── */
    const [file,       setFile]       = useState(null);
    const [selBank,    setSelBank]    = useState(null);
    const [selSys,     setSelSys]     = useState(null);
    const [filterBank, setFilterBank] = useState('');
    const [filterSys,  setFilterSys]  = useState('');

    const today    = new Date().toISOString().split('T')[0];
    const firstDay = new Date(
        new Date().getFullYear(), new Date().getMonth(), 1
    ).toISOString().split('T')[0];

    const [dateStart, setDateStart] = useState(firstDay);
    const [dateEnd,   setDateEnd]   = useState(today);
    const [pairs,     setPairs]     = useState([]);

    /* ── Persist ─────────────────────────────────────────────── */
    useEffect(() => { localStorage.setItem(STORAGE_BANK, JSON.stringify(bankTxs)); }, [bankTxs]);
useEffect(() => { localStorage.setItem(STORAGE_SYS,  JSON.stringify(sysTxs));  }, [sysTxs]);
useEffect(() => { localStorage.setItem(STORAGE_HIST, JSON.stringify(history));  }, [history]);

    /* ── Detect BaaS ─────────────────────────────────────────── */
    useEffect(() => {
        api.get('/api/baas/wallet')
            .then(({ data }) => {
                if (data?.wallet?.status === 'ACTIVE') {
                    setHasBaas(true);
                    setBaasWallet(data.wallet);
                }
            })
            .catch(() => {
                setHasBaas(false);
                setBaasWallet(null);
            });
    }, []);

    /* ── Fetch system txs ────────────────────────────────────── */
    const fetchSys = useCallback(async (start, end) => {
    setLoading(true);
    try {
        const { data } = await api.post('/api/financeiro/conciliacao/buscar-transacoes', {
            dataInicio: start || dateStart,
            dataFim:    end   || dateEnd,
            valores:    bankTxs.map(t => t.valor),
        });
        // Sempre substitui — nunca acumula dados de syncs anteriores.
        // O merge acumulativo era a segunda causa do cross-tenant leak.
        setSysTxs(
            [...data].sort(
                (a, b) => new Date(a.data_ref || a.data) - new Date(b.data_ref || b.data)
            )
        );
    } catch {
        toast.error('Erro ao buscar transações do sistema.');
    } finally {
        setLoading(false);
    }
}, [dateStart, dateEnd, bankTxs]);

    /* ── Polling automático ──────────────────────────────────── */
    useEffect(() => {
        if (!pollingActive) {
            setPollCountdown(POLL_INTERVAL);
            return;
        }

        setPollCountdown(POLL_INTERVAL);

        // Decrementa o contador visual a cada segundo
        const ticker = setInterval(() => {
            setPollCountdown(s => (s <= 1 ? POLL_INTERVAL : s - 1));
        }, 1000);

        // Dispara o fetchSys a cada POLL_INTERVAL segundos
        const poller = setInterval(() => {
            if (!loading) {
                fetchSys().then(() => {
                    setLastSync(new Date());
                    setPollCountdown(POLL_INTERVAL);
                });
            }
        }, POLL_INTERVAL * 1000);

        return () => {
            clearInterval(ticker);
            clearInterval(poller);
        };
    }, [pollingActive]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── URL redirect (FechamentoFinanceiro) ─────────────────── */
    useEffect(() => {
        const p = new URLSearchParams(location.search);
        const d = p.get('data');
        if (d) {
            setDateStart(d);
            setDateEnd(d);
            setFilterBank('');
            setFilterSys('');
            fetchSys(d, d);
            toast.info(`Auditoria direcionada para ${fmtDate(d)}`);
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Upload OFX ──────────────────────────────────────────── */
    const handleUpload = async () => {
        if (!file) return toast.warn('Selecione um arquivo .ofx');
        setLoading(true);
        const fd = new FormData();
        fd.append('extrato', file);
        try {
            const { data } = await api.post('/api/financeiro/conciliacao/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const novas = data.map(tx => ({
                ...tx,
                frontendId: tx.id || `tmp-${Date.now()}-${Math.random()}`,
                conciliado: false,
                origem:     'ofx',
            }));
            setBankTxs(prev => {
                const ids       = new Set(prev.map(t => t.id));
                const filtradas = novas.filter(t => !ids.has(t.id));
                if (!filtradas.length) { toast.info('Sem transações novas.'); return prev; }
                toast.success(`${filtradas.length} transações importadas!`);
                return [...prev, ...filtradas].sort((a, b) => new Date(a.data) - new Date(b.data));
            });
        } catch {
            toast.error('Erro ao processar OFX.');
        } finally {
            setLoading(false);
            setFile(null);
        }
    };

    /* ── Importar créditos BaaS ──────────────────────────────── */
    const handleImportBaas = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/baas/receivable-feed', {
                params: {
                    dataInicio: dateStart || undefined,
                    dataFim:    dateEnd   || undefined,
                },
            });
            if (!data.length) {
                toast.info('Nenhum recebimento da Conta Digital disponível para o período.');
                return;
            }
            setBankTxs(prev => {
                const existingIds = new Set(prev.map(t => t.frontendId));
                const novas       = data.filter(t => !existingIds.has(t.frontendId));
                if (!novas.length) {
                    toast.info('Sem transações BaaS novas para o período selecionado.');
                    return prev;
                }
                toast.success(`${novas.length} recebimento(s) da Conta Digital importado(s)!`);
                return [...prev, ...novas].sort((a, b) => new Date(a.data) - new Date(b.data));
            });
        } catch {
            toast.error('Erro ao importar transações da Conta Digital.');
        } finally {
            setLoading(false);
        }
    };

    /* ── Reconciliação automática BaaS ───────────────────────── */
    const handleBaasAutoReconcile = async () => {
        if (!window.confirm(
            'Executar reconciliação automática da Conta Digital agora?\n\n' +
            'Contas a Receber com correspondência de valor/data serão baixadas automaticamente.'
        )) return;
        setBaasReconciling(true);
        try {
            const { data } = await api.post('/api/baas/auto-reconcile');
            if (data.matched > 0) {
                toast.success(`${data.matched} conta(s) a receber baixada(s) automaticamente via Conta Digital!`);
                await fetchSys();
            } else {
                toast.info('Nenhum par encontrado para reconciliação automática.');
            }
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao executar reconciliação automática.');
        } finally {
            setBaasReconciling(false);
        }
    };

    /* ── Auto-match engine ───────────────────────────────────── */
    useEffect(() => {
        if (!bankTxs.length || !sysTxs.length) { setPairs([]); return; }
        const avail = [...sysTxs];
        const found = [];
        bankTxs.forEach(b => {
            const bv  = Math.abs(parseFloat(b.valor)).toFixed(2);
            const bd  = normDate(b.data);
            const idx = avail.findIndex(s => {
                const sv = Math.abs(parseFloat(s.valor)).toFixed(2);
                const sd = normDate(s.data_ref || s.data);
                return sv === bv && Math.abs((bd - sd) / 86400000) <= 4;
            });
            if (idx !== -1) {
                found.push({ bankId: b.frontendId, sysId: avail[idx].id });
                avail.splice(idx, 1);
            }
        });
        setPairs(found);
    }, [bankTxs, sysTxs]);

    /* ── Auto reconcile em lote ──────────────────────────────── */
    const handleAutoReconcile = async () => {
        if (!pairs.length) return toast.info('Nenhum par para automação.');
        if (!window.confirm(`Conciliar ${pairs.length} par(es) automaticamente?`)) return;
        setLoading(true);
        try {
            const batch = [];
            for (const p of pairs) {
                const b = bankTxs.find(t => t.frontendId === p.bankId);
                const s = sysTxs.find(t => t.id === p.sysId);
                await api.post('/api/financeiro/conciliacao/confirmar', {
                    financeiroId: s.id,
                    origem:       s.origem_tipo || 'caixa',
                });
                batch.push({ bank: b, system: s, reconciledAt: new Date().toISOString() });
            }
            setHistory(prev => [...batch, ...prev]);
            const bIds = new Set(pairs.map(p => p.bankId));
            const sIds = new Set(pairs.map(p => p.sysId));
            setBankTxs(prev => prev.filter(t => !bIds.has(t.frontendId)));
            setSysTxs(prev  => prev.filter(t => !sIds.has(t.id)));
            setPairs([]);
            setShowStamp(true);
            setTimeout(() => setShowStamp(false), 2000);
            toast.success(`${batch.length} par(es) conciliados!`);
        } catch {
            toast.error('Erro na automação.');
        } finally {
            setLoading(false);
        }
    };

    /* ── Manual reconcile ────────────────────────────────────── */
    const doReconcile = async (b, s) => {
        const bank = b || selBank;
        const sys  = s || selSys;
        if (!bank || !sys) return toast.warn('Selecione um item em cada coluna.');

        const diff = Math.abs(bank.valor) - Math.abs(sys.valor);
        if (Math.abs(diff) > 0.01 && !showAdjust) {
            setShowAdjust(true);
            return;
        }

        try {
            setLoading(true);
            await api.post('/api/financeiro/conciliacao/confirmar', {
                financeiroId:   sys.id,
                origem:         sys.origem_tipo || 'caixa',
                ajuste:         showAdjust ? adjustType : null,
                valorDiferenca: diff,
            });
            setHistory(prev => [
                { bank, system: sys, reconciledAt: new Date().toISOString() },
                ...prev,
            ]);
            setBankTxs(prev => prev.filter(t => t.frontendId !== bank.frontendId));
            setSysTxs(prev  => prev.filter(t => t.id !== sys.id));
            setSelBank(null);
            setSelSys(null);
            setShowAdjust(false);
            setShowStamp(true);
            setTimeout(() => setShowStamp(false), 1800);
            toast.success('Conciliado com sucesso!');
        } catch {
            toast.error('Erro ao conciliar.');
        } finally {
            setLoading(false);
        }
    };

    /* ── Helpers ─────────────────────────────────────────────── */
    const removeBankTx = (id) => {
        if (window.confirm('Remover esta linha do extrato?')) {
            setBankTxs(prev => prev.filter(t => t.frontendId !== id));
            if (selBank?.frontendId === id) setSelBank(null);
        }
    };

    const handleClear = () => {
    if (window.confirm('Limpar área de trabalho?')) {
        setBankTxs([]);
        setSysTxs([]);
        localStorage.removeItem(STORAGE_BANK);
        localStorage.removeItem(STORAGE_SYS);
        setPairs([]);
        toast.info('Área resetada.');
    }
};

    const handleManualSync = () => {
        fetchSys();
        setLastSync(new Date());
    };

    const getMatch = (id, side) => {
        const pair = pairs.find(p => side === 'bank' ? p.bankId === id : p.sysId === id);
        if (!pair) return null;
        return side === 'bank'
            ? sysTxs.find(s => s.id === pair.sysId)
            : bankTxs.find(b => b.frontendId === pair.bankId);
    };

    /* ── Derived ─────────────────────────────────────────────── */
    const filtBank = useMemo(
        () => bankTxs.filter(t => t.descricao?.toLowerCase().includes(filterBank.toLowerCase())),
        [bankTxs, filterBank]
    );
    const filtSys = useMemo(
        () => sysTxs.filter(t => t.descricao?.toLowerCase().includes(filterSys.toLowerCase())),
        [sysTxs, filterSys]
    );

    const bankTotal = useMemo(() => filtBank.reduce((a, t) => a + t.valor, 0), [filtBank]);
    const sysTotal  = useMemo(
        () => filtSys.reduce((a, t) => a + (t.tipo === 'Despesa' && t.valor > 0 ? -t.valor : t.valor), 0),
        [filtSys]
    );

    const bankOFX  = useMemo(() => filtBank.filter(t => t.origem !== 'baas_wallet').length, [filtBank]);
    const bankBaas = useMemo(() => filtBank.filter(t => t.origem === 'baas_wallet').length,  [filtBank]);

    const readyToReconcile = !!(selBank && selSys);

    const TABS = [
        { id: 'diario', label: 'Diário'  },
        { id: 'mensal', label: 'Mensal'  },
        { id: 'anual',  label: 'Anual'   },
    ];

    /* ═══════════════════════════════════════════════════════════
       RENDER
    ═══════════════════════════════════════════════════════════ */
    return (
        <div className="cnc-root">
            {showStamp && <StampEffect />}
            {loading   && <LoadingOverlay />}

            {/* ── HEADER ─────────────────────────────────────── */}
            <header className="cnc-header">
                <div className="cnc-header-brand">
                    <div className="cnc-brand-icon"><FaUniversity /></div>
                    <div>
                        <h1>Conciliação Bancária</h1>
                        <p>Interface de Conformidade · BLB System</p>
                    </div>
                </div>

                <nav className="cnc-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            className={`cnc-tab ${viewMode === t.id ? 'active' : ''}`}
                            onClick={() => setViewMode(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>

                <div className="cnc-header-actions">
                    <button
                        className={`cnc-kpi-badge ${showKpiBar ? 'active' : ''}`}
                        title="Clique para ver os indicadores e totais"
                        onClick={() => setShowKpiBar(!showKpiBar)}
                    >
                        <FaChartBar />
                        <div>
                            <span>Resumo Geral</span>
                            <strong>Indicadores</strong>
                        </div>
                    </button>

                    {hasBaas && baasWallet && (
                        <button
                            className={`cnc-baas-badge ${showBaasBar ? 'active' : ''}`}
                            title="Clique para gerenciar a Conta Digital"
                            onClick={() => setShowBaasBar(!showBaasBar)}
                        >
                            <FaUniversity />
                            <div>
                                <span>Conta Digital</span>
                                <strong>{fmtCurrency(baasWallet.saldo_disponivel)}</strong>
                            </div>
                        </button>
                    )}

                    <button className="btn-hdr ghost" onClick={() => setShowHistory(true)}>
                        <FaArchive /> Arquivo Digital
                        {history.length > 0 && <span className="hdr-badge">{history.length}</span>}
                    </button>
                    <button className="btn-hdr danger" onClick={handleClear}>
                        <FaEraser />
                    </button>
                </div>
            </header>

            {/* ── KPI STRIP ──────────────────────────────────── */}
            {showKpiBar && (
                <div className="cnc-kpi-strip">
                    <KpiChip
                        icon={FaWallet}
                        label={hasBaas && bankBaas > 0 ? `Extrato (${bankBaas} BaaS)` : 'Ext. Bancário'}
                        value={filtBank.length}
                        accent="#2563eb"
                    />
                    <KpiChip icon={FaFileInvoiceDollar} label="Registros CRM"   value={filtSys.length}  accent="#0891b2" />
                    <KpiChip icon={FaBolt}               label="Pares Sugeridos" value={pairs.length}    accent="#d97706" />
                    <KpiChip icon={FaCheckCircle}         label="Conciliados"     value={history.length} accent="#059669" />
                    <div className="kpi-totals">
                        <div className={`kpi-total-item ${bankTotal >= 0 ? 'pos' : 'neg'}`}>
                            <span>Total Extrato</span>
                            <strong>{fmtCurrency(bankTotal)}</strong>
                        </div>
                        <div className="kpi-total-sep" />
                        <div className={`kpi-total-item ${sysTotal >= 0 ? 'pos' : 'neg'}`}>
                            <span>Total CRM</span>
                            <strong>{fmtCurrency(sysTotal)}</strong>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CONTROLS ───────────────────────────────────── */}
            <div className="cnc-controls">

                {/* Card OFX */}
                <div className="ctrl-card upload-card">
                    <label className={`upload-zone ${file ? 'has-file' : ''}`}>
                        <input
                            type="file"
                            accept=".ofx"
                            onChange={e => setFile(e.target.files[0])}
                            hidden
                        />
                        <FaUpload className="uz-icon" />
                        <span>{file ? file.name : 'Importar Extrato (.OFX)'}</span>
                    </label>
                    <button
                        className={`btn-process ${file ? 'ready' : ''}`}
                        onClick={handleUpload}
                        disabled={!file || loading}
                    >
                        Processar
                    </button>
                </div>

                {/* Card Sincronização CRM + Polling */}
                <div className="ctrl-card search-card">
                    <div className="date-range">
                        <FaCalendarAlt className="dr-icon" />
                        <input
                            type="date"
                            value={dateStart}
                            onChange={e => setDateStart(e.target.value)}
                        />
                        <span className="dr-sep">→</span>
                        <input
                            type="date"
                            value={dateEnd}
                            onChange={e => setDateEnd(e.target.value)}
                        />
                    </div>

                    <div className="sync-controls">
                        <button
                            className="btn-sync"
                            onClick={handleManualSync}
                            disabled={loading}
                        >
                            <FaSync className={loading ? 'spin-icon' : ''} /> Sincronizar CRM
                        </button>
                        <button
                            className={`btn-poll ${pollingActive ? 'poll-on' : 'poll-off'}`}
                            onClick={() => setPollingActive(p => !p)}
                            title={pollingActive ? 'Pausar auto-sync' : 'Ativar auto-sync (60s)'}
                        >
                            {pollingActive ? <FaPause size={13} /> : <FaPlay size={13} />}
                            {pollingActive ? 'Pausar' : 'Auto'}
                        </button>
                    </div>

                    {/* Status bar do polling */}
                    {(pollingActive || lastSync) && (
                        <div className={`poll-status-bar ${pollingActive ? 'active' : 'idle'}`}>
                            {pollingActive ? (
                                <div className="poll-status-inner">
                                    <div className="poll-status-text">
                                        <span className="poll-pulse" />
                                        <span>
                                            Próxima sync em <strong>{pollCountdown}s</strong>
                                        </span>
                                        {lastSync && (
                                            <span className="poll-lastsync">
                                                · Última: {lastSync.toLocaleTimeString('pt-BR', {
                                                    hour:   '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="poll-track">
                                        <div
                                            className="poll-fill"
                                            style={{
                                                width: `${((POLL_INTERVAL - pollCountdown) / POLL_INTERVAL) * 100}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <span className="poll-lastsync-idle">
                                    Última sync: {lastSync.toLocaleTimeString('pt-BR', {
                                        hour:   '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                    })}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Card Conta Digital BaaS */}
                {hasBaas && showBaasBar && (
                    <div className="ctrl-card baas-card">
                        <div className="baas-ctrl-header">
                            <FaUniversity className="baas-ctrl-icon" />
                            <span className="baas-ctrl-label">Conta Digital</span>
                            {baasWallet && (
                                <span className="baas-ctrl-saldo">
                                    {fmtCurrency(baasWallet.saldo_disponivel)}
                                </span>
                            )}
                        </div>
                        <div className="baas-ctrl-actions">
                            <button
                                className="btn-sync baas-import"
                                onClick={handleImportBaas}
                                disabled={loading}
                                title="Carregar recebimentos da Conta Digital no extrato"
                            >
                                <FaUniversity /> Importar BaaS
                            </button>
                            <button
                                className="btn-auto baas-auto"
                                onClick={handleBaasAutoReconcile}
                                disabled={baasReconciling || loading}
                                title="Baixar automaticamente contas a receber com correspondência na Conta Digital"
                            >
                                {baasReconciling ? <FaSpinner className="spin-icon" /> : <FaBolt />}
                                Auto-Baixa
                            </button>
                        </div>
                        <p className="baas-ctrl-hint">
                            O cron roda automaticamente às 08h, 14h e 20h
                        </p>
                    </div>
                )}
            </div>

            {/* ── AUTOMATION BAR ─────────────────────────────── */}
            {pairs.length > 0 && (
                <div className="cnc-auto-bar">
                    <div className="auto-bar-left">
                        <div className="auto-icon"><FaBolt /></div>
                        <div>
                            <strong>Motor de Correspondência Ativo</strong>
                            <span>
                                {pairs.length} par{pairs.length !== 1 ? 'es' : ''} com
                                correspondência exata detectado{pairs.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                    <button className="btn-auto" onClick={handleAutoReconcile} disabled={loading}>
                        <FaCheckCircle /> Executar Automação em Lote
                        <span className="auto-count">{pairs.length}</span>
                    </button>
                </div>
            )}

            {/* ── WORKSPACE ──────────────────────────────────── */}
            <main className="cnc-workspace">

                {/* BANK COLUMN */}
                <section className="cnc-col">
                    <div className="col-head bank-head">
                        <div className="col-head-title">
                            <FaWallet />
                            <h3>Extrato Bancário</h3>
                            {hasBaas && bankBaas > 0 && bankOFX > 0 && (
                                <span className="col-head-sub">
                                    {bankOFX} OFX · {bankBaas} BaaS
                                </span>
                            )}
                        </div>
                        <span className={`col-total ${bankTotal >= 0 ? 'pos' : 'neg'}`}>
                            {fmtCurrency(bankTotal)}
                        </span>
                    </div>
                    <div className="col-filter">
                        <FaFilter />
                        <input
                            type="text"
                            placeholder="Filtrar descrição..."
                            value={filterBank}
                            onChange={e => setFilterBank(e.target.value)}
                        />
                        {filterBank && (
                            <button className="filter-clear" onClick={() => setFilterBank('')}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    <div className="col-body">
                        {filtBank.length === 0 ? (
                            <div className="col-empty">
                                <FaUpload />
                                <p>Importe um extrato .OFX ou carregue a Conta Digital</p>
                            </div>
                        ) : (
                            filtBank.map(tx => (
                                <BankTxCard
                                    key={tx.frontendId}
                                    tx={tx}
                                    isSelected={selBank?.frontendId === tx.frontendId}
                                    isMatch={!!getMatch(tx.frontendId, 'bank')}
                                    matchTarget={getMatch(tx.frontendId, 'bank')}
                                    onSelect={() => setSelBank(s =>
                                        s?.frontendId === tx.frontendId ? null : tx
                                    )}
                                    onQuickMatch={doReconcile}
                                    onRemove={removeBankTx}
                                />
                            ))
                        )}
                    </div>
                </section>

                {/* BRIDGE */}
                <div className="cnc-bridge">
                    <div className="bridge-line" />
                    <div className={`bridge-btn-wrap ${readyToReconcile ? 'ready' : ''}`}>
                        <button
                            className={`bridge-btn ${readyToReconcile ? 'ready' : 'idle'}`}
                            onClick={() => doReconcile()}
                            disabled={!readyToReconcile}
                            title={readyToReconcile ? 'Conciliar selecionados' : 'Selecione um item em cada coluna'}
                        >
                            <FaHandshake className="bridge-icon" />
                        </button>
                        <span className="bridge-label">
                            {readyToReconcile ? 'CONCILIAR' : 'SELECIONE'}
                        </span>
                    </div>
                    <div className="bridge-indicators">
                        <div className={`bridge-ind ${selBank ? 'sel' : ''}`}>
                            <div className="bind-dot" />
                            <span>
                                {selBank ? selBank.descricao.slice(0, 18) + '…' : 'Extrato'}
                            </span>
                        </div>
                        <div className={`bridge-ind ${selSys ? 'sel' : ''}`}>
                            <div className="bind-dot" />
                            <span>
                                {selSys ? selSys.descricao.slice(0, 18) + '…' : 'CRM'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* SYSTEM COLUMN */}
                <section className="cnc-col">
                    <div className="col-head sys-head">
                        <div className="col-head-title">
                            <FaFileInvoiceDollar />
                            <h3>Registros CRM</h3>
                        </div>
                        <span className={`col-total ${sysTotal >= 0 ? 'pos' : 'neg'}`}>
                            {fmtCurrency(sysTotal)}
                        </span>
                    </div>
                    <div className="col-filter">
                        <FaFilter />
                        <input
                            type="text"
                            placeholder="Filtrar nome ou título..."
                            value={filterSys}
                            onChange={e => setFilterSys(e.target.value)}
                        />
                        {filterSys && (
                            <button className="filter-clear" onClick={() => setFilterSys('')}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    <div className="col-body">
                        {filtSys.length === 0 ? (
                            <div className="col-empty">
                                <FaChartBar />
                                <p>Selecione um período e clique em Sincronizar CRM</p>
                            </div>
                        ) : (
                            filtSys.map(tx => (
                                <SystemTxCard
                                    key={tx.id}
                                    tx={tx}
                                    isSelected={selSys?.id === tx.id}
                                    isMatch={!!getMatch(tx.id, 'system')}
                                    onSelect={() => setSelSys(s =>
                                        s?.id === tx.id ? null : tx
                                    )}
                                />
                            ))
                        )}
                    </div>
                </section>
            </main>

            {/* ── MODALS ─────────────────────────────────────── */}
            {showHistory && (
                <HistoryModal history={history} onClose={() => setShowHistory(false)} />
            )}
            {showAdjust && (
                <AdjustModal
                    onConfirm={() => doReconcile()}
                    onClose={() => setShowAdjust(false)}
                    adjustmentType={adjustType}
                    setAdjustmentType={setAdjustType}
                />
            )}
        </div>
    );
};

export default ConciliacaoBancaria;